import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions';
import AdminLayout from '../../layouts/AdminLayout';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import { MdAdd, MdEdit } from 'react-icons/md';
import { fetch_all_reports } from '../../api/locationsAPI';

export default function LocationReportsPage() {
    const navigate = useNavigate();
    useFetchAuthorisation("user");
    useFetchUserData();

    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [priorityFilter, setPriorityFilter] = useState("all");

    const loadReports = async () => {
        setLoading(true);
        const params = {};
        if (statusFilter !== "all") params.status = statusFilter;
        if (priorityFilter !== "all") params.priority = priorityFilter;

        const res = await fetch_all_reports(params);
        if (res?.status === 200) {
            setReports(res.data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadReports();
    }, [statusFilter, priorityFilter]);

    const columns = [
        {
            header: "Report Title & Category",
            accessor: "title",
            modifier: (val, row) => (
                <div>
                    <span className="font-bold text-gray-900">{row.title}</span>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                            {row.report_type}
                        </span>
                        <span className="text-xs text-gray-500 font-medium">
                            {row.location_data?.name || `Location #${row.location_id}`}
                        </span>
                    </div>
                </div>
            )
        },
        {
            header: "Priority",
            accessor: "priority",
            modifier: (val) => {
                const colors = {
                    urgent: "bg-red-100 text-red-800 border-red-200",
                    high: "bg-orange-100 text-orange-800 border-orange-200",
                    medium: "bg-amber-100 text-amber-800 border-amber-200",
                    low: "bg-emerald-100 text-emerald-800 border-emerald-200",
                };
                return (
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase border ${colors[val] || "bg-gray-100 text-gray-800"}`}>
                        {val}
                    </span>
                );
            }
        },
        {
            header: "Status",
            accessor: "status",
            modifier: (val) => {
                const colors = {
                    open: "bg-amber-100 text-amber-800",
                    in_progress: "bg-blue-100 text-blue-800",
                    resolved: "bg-emerald-100 text-emerald-800",
                    closed: "bg-gray-100 text-gray-800",
                };
                return (
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold capitalize ${colors[val] || "bg-gray-100 text-gray-800"}`}>
                        {val ? val.replace("_", " ") : "open"}
                    </span>
                );
            }
        },
        {
            header: "Assigned To",
            accessor: "assignee_data",
            modifier: (val, row) => (
                <span className="text-xs font-semibold text-gray-800">
                    {row.assignee_data ? `${row.assignee_data.first_name} ${row.assignee_data.last_name}` : "Unassigned"}
                </span>
            )
        },
        {
            header: "Reported By",
            accessor: "reporter_data",
            modifier: (val, row) => (
                <span className="text-xs text-gray-600">
                    {row.reporter_data ? `${row.reporter_data.first_name} ${row.reporter_data.last_name}` : "Unknown"}
                </span>
            )
        },
    ];

    return (
        <AdminLayout
            navSelected={"Maintenance Issues"}
            headerText={"Maintenance Issues"}
            toolbar={
                <>
                    <div className="flex items-center gap-3">
                        <h1 className="text-xl font-bold text-gray-900">Location Maintenance Reports</h1>
                    </div>
                    <Button variant="primary" icon={MdAdd} onClick={() => navigate('/admin/locations/reports/details?report_id=new')}>
                        Report Issue
                    </Button>
                </>
            }
        >
            {loading ? (
                <div className="p-8 text-center text-gray-500 font-medium">Loading maintenance reports...</div>
            ) : (
                <DataTable
                    columns={columns}
                    data={reports}
                    enableActions
                    actions={(row) => (
                        <Button
                            variant="outline"
                            size="sm"
                            icon={MdEdit}
                            onClick={() => navigate(`/admin/locations/reports/details?report_id=${row.id}`)}
                        >
                            Manage
                        </Button>
                    )}
                />
            )}
        </AdminLayout>
    );
}
